import type { WorkspaceRole } from '@/lib/settings';
import React, { useId } from 'react';
import { Input as UIInput } from '@/components/ui/input';
import {
  Select as UISelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar as UIAvatar, AvatarFallback } from '@/components/ui/avatar';


import { AlertBanner, AlertBannerAction } from '@/components/molecules/AlertBanner';
import { cn } from '@/lib/utils';
import * as I from '@/icons/Icon';
import { getAvatarColor, getAvatarInitial } from '@/lib/avatar-color';

/**
 * Shared between every settings section. These are not part of the public
 * design system — they're legacy-shaped adapters over shadcn primitives.
 * Once each call site is migrated to use the native shadcn shape directly we
 * can delete this file.
 *
 * This file exports only React components. Non-component infrastructure
 * (palettes, role options, `capitalize`) lives in `./common-helpers` so this
 * module stays a Vite Fast Refresh boundary.
 */

// ── Legacy-shaped adapters over shadcn primitives ────────────────────────────

type IconCmp = React.ComponentType<{ size?: number }>;

export function Input({
  value,
  defaultValue,
  onChange,
  onBlur,
  placeholder,
  type = 'text',
  icon: Icon,
  suffix,
  readOnly,
  disabled,
  ariaLabel,
  style,
  invalid,
  id,
  size = 'default',
}: {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password';
  icon?: IconCmp;
  suffix?: React.ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  style?: React.CSSProperties;
  invalid?: boolean;
  /** Input id, to pair with a visible `<Label htmlFor>`. */
  id?: string;
  /** `sm` is the 32px field — same height as the `size="sm"` buttons beside it. */
  size?: 'default' | 'sm';
}) {
  const isControlled = value !== undefined;
  return (
    <div className="relative flex w-full items-center" style={style}>
      {Icon && (
        <span className="pointer-events-none absolute left-3 inline-flex text-[color:var(--ink-400)]">
          <Icon size={14} />
        </span>
      )}
      <UIInput
        id={id}
        {...(isControlled ? { value: value ?? '' } : { defaultValue })}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        type={type}
        placeholder={placeholder}
        readOnly={readOnly || (!onChange && isControlled)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        className={cn(
          size === 'sm' && 'h-8 px-2.5 py-1',
          Icon && 'pl-9',
          suffix && 'pr-28',
          invalid && 'border-destructive',
        )}
      />
      {suffix && <span className="absolute right-2 inline-flex">{suffix}</span>}
    </div>
  );
}

export function Select({
  value,
  options,
  onChange,
  width = 140,
  disabled,
  ariaLabel,
  label,
  id,
  style,
}: {
  value: string;
  options?: { value: string; label: string }[];
  onChange?: (value: string) => void;
  width?: number;
  disabled?: boolean;
  /** Screen-reader-only name for the trigger. Ignored (and redundant) once
   * `label` is set — that renders a real, associated `<Label>` instead, and
   * setting both would double-announce the name. */
  ariaLabel?: string;
  /** Visible label rendered above the trigger, associated via `htmlFor`/`id`
   * (same pattern as `SmtpConnectFields`'s `Field`). Prefer this over
   * `ariaLabel` for any combobox that isn't obviously named by surrounding
   * context. */
  label?: string;
  /** Trigger id to pair with `label`'s `htmlFor`. Auto-generated when
   * `label` is set and `id` is omitted. */
  id?: string;
  style?: React.CSSProperties;
}) {
  const generatedId = useId();
  const triggerId = label ? (id ?? `select-${generatedId}`) : id;

  let trigger: React.ReactNode;
  if (!options || !onChange) {
    const optionLabel = options?.find((o) => o.value === value)?.label ?? value;
    trigger = (
      <button
        type="button"
        id={triggerId}
        aria-label={label ? undefined : (ariaLabel ?? value)}
        disabled
        className="flex h-10 cursor-not-allowed items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm opacity-85"
        style={{ width, ...style }}
      >
        <span className="truncate">{optionLabel}</span>
        <I.ChevronDown size={14} />
      </button>
    );
  } else {
    trigger = (
      <UISelect value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          id={triggerId}
          style={{ width, ...style }}
          aria-label={label ? undefined : ariaLabel}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </UISelect>
    );
  }

  if (!label) return trigger;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={triggerId} className="text-xs">
        {label}
      </Label>
      {trigger}
    </div>
  );
}

const CHIP_TONE_TO_VARIANT = {
  neutral: 'neutral',
  good: 'success',
  warn: 'warning',
  bad: 'destructive',
  info: 'info',
  brand: 'brand',
} as const;

export function Chip({
  tone = 'neutral',
  size = 'md',
  icon,
  children,
  style,
}: {
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info' | 'brand';
  size?: 'sm' | 'md';
  icon?: 'dot' | React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const iconSize = size === 'sm' ? 11 : 12;
  return (
    <Badge
      variant={CHIP_TONE_TO_VARIANT[tone]}
      className={cn(
        'gap-1.5 font-medium',
        size === 'sm' ? 'h-5 px-1.5 text-[10.5px]' : 'h-6 px-2 text-[11.5px]',
      )}
      style={style}
    >
      {icon === 'dot' && (
        <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
      )}
      {icon && icon !== 'dot' && (
        <span aria-hidden className="inline-flex">
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<{ size?: number }>, { size: iconSize })
            : icon}
        </span>
      )}
      {children}
    </Badge>
  );
}

export function Avatar({
  name,
  size = 28,
  color,
  style,
}: {
  name: string;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}) {
  const letter = getAvatarInitial(name);
  const bg = color ?? getAvatarColor(name);
  return (
    <UIAvatar style={{ width: size, height: size, ...style }} className="shrink-0">
      <AvatarFallback
        className="font-semibold"
        style={{
          background: bg,
          color: 'var(--ink-800)',
          fontSize: Math.round(size * 0.4),
          boxShadow: 'inset 0 0 0 1px rgba(20,15,10,.06)',
        }}
      >
        {letter}
      </AvatarFallback>
    </UIAvatar>
  );
}

// ── New section primitives (warm-productivity tabbed layout) ────────────────
//
// Each section is one or more cards. A `SectionGroup` is the page-level
// rhythm unit: an uppercase mono eyebrow above a single card, separated by
// 24px from the next group. Cards are flat — `paper + ink-200 hairline border,
// no shadow` — to match the /email-accounts table surface.

/**
 * Uppercase mono eyebrow label. The base typographic recipe (11px, 600,
 * `--ink-400`, uppercase, `.2em` tracking) is shared; callers tune the
 * surrounding margin via `style` since it varies by context (0, 0 0 8px,
 * 0 0 10px).
 */
type SectionEyebrowProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

export function SectionEyebrow({ children, className, style }: SectionEyebrowProps) {
  return (
    <p
      className={cn('mono', className)}
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--ink-400)',
        textTransform: 'uppercase',
        letterSpacing: '.2em',
        ...style,
      }}
    >
      {children}
    </p>
  );
}

type SectionGroupProps = {
  eyebrow: string;
  /** Optional `--bad-600` eyebrow treatment for the danger zone. */
  tone?: 'default' | 'danger';
  /** Optional trailing control (e.g. a section-level action button) rendered
   * on the eyebrow line, right-aligned. Use for an action that applies to
   * the section as a whole rather than any one row within it — it stays in
   * one fixed spot regardless of what the section's rows re-render to. */
  action?: React.ReactNode;
  children: React.ReactNode;
};

export function SectionGroup({ eyebrow, tone = 'default', action, children }: SectionGroupProps) {
  return (
    <div>
      <div
        className="flex items-center justify-between gap-3"
        style={{ margin: '0 0 8px' }}
      >
        <p
          className="mono"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: tone === 'danger' ? 'var(--bad-600)' : 'var(--ink-400)',
            textTransform: 'uppercase',
            letterSpacing: '.2em',
            margin: 0,
          }}
        >
          {eyebrow}
        </p>
        {action}
      </div>
      {children}
    </div>
  );
}

type SectionCardProps = {
  /** When true, removes the inner card padding so rows touch the edge. */
  tight?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
};

export function SectionCard({ tight, className, style, children }: SectionCardProps) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--paper)',
        borderRadius: 'var(--r-lg)',
        // `paper + ink-200 hairline border, no shadow` — the block comment
        // above `SectionEyebrow` documents this as matching the
        // /email-accounts table surface; keep the two in sync.
        border: '1px solid var(--ink-200)',
        padding: tight ? 0 : 18,
        overflow: tight ? 'hidden' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * One row inside a `SectionCard tight` — label/help on the left, trailing
 * control on the right. Mirrors the `.row` recipe from the visual mockup.
 */
type SectionRowProps = {
  label: React.ReactNode;
  help?: React.ReactNode;
  trailing?: React.ReactNode;
  /** When true the row uses `--brand-50` background (current workspace, current session). */
  current?: boolean;
  /** When true the row hides its bottom border (last row). */
  last?: boolean;
  /** Override row padding (used when nesting inside a non-tight card). */
  padding?: string;
  /** When the trailing area is full-width (eg "Sign out of all others" footer). */
  muted?: boolean;
};

export function SectionRow({
  label,
  help,
  trailing,
  current,
  last,
  padding = '12px 18px',
  muted,
}: SectionRowProps) {
  return (
    <div
      className="flex items-center gap-3.5"
      style={{
        minHeight: 56,
        padding,
        background: current ? 'var(--brand-50)' : muted ? 'var(--ink-50)' : undefined,
        borderBottom: last ? '0' : '1px solid var(--ink-100)',
      }}
    >
      <div className="min-w-0 flex-1">
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--ink-900)',
            lineHeight: 1.35,
          }}
        >
          {label}
        </div>
        {help && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-500)',
              lineHeight: 1.45,
              marginTop: 2,
            }}
          >
            {help}
          </div>
        )}
      </div>
      {trailing && <div className="flex shrink-0 items-center gap-2">{trailing}</div>}
    </div>
  );
}

/**
 * A vertically-stacked label + control field: label above a full-width
 * control, with an optional help line below it. Mirrors the mockup's
 * `.field` recipe — distinct from `SectionRow`'s label-left/control-right
 * row shape, which is for listing an item with a trailing action rather than
 * editing the section's own value directly (Profile's name/email, General's
 * workspace name).
 */
type SectionFieldProps = {
  label: string;
  help?: React.ReactNode;
  children: React.ReactNode;
};

export function SectionField({ label, help, children }: SectionFieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-700)' }}>{label}</span>
      {children}
      {help && (
        <span style={{ fontSize: 11.5, color: 'var(--ink-500)' }}>{help}</span>
      )}
    </label>
  );
}

/**
 * Inline error + retry block — every fetch site shows this on `isError`.
 */
type RetryBlockProps = {
  message: string;
  onRetry: () => void;
};

export function RetryBlock({ message, onRetry }: RetryBlockProps) {
  return (
    <AlertBanner
      variant="error"
      className="p-3"
      action={
        <AlertBannerAction onClick={onRetry}>Retry</AlertBannerAction>
      }
    >
      {message}
    </AlertBanner>
  );
}

/**
 * The role pill used across Team/People/invite lists. Mirrors `.pill` from
 * the visual mockup exactly — `owner` is a tinted brand pill, `admin` is
 * brand-fill, `editor` is warn-tinted, `viewer` is neutral-tinted.
 */
type RoleBadgeProps = {
  role: WorkspaceRole;
};

// `owner` sits above admin and is the only company-wide role. Typed against
// `WorkspaceRole` rather than a local union so a future role added to the
// ladder fails the build here instead of rendering an unstyled badge.
const ROLE_BADGE_STYLE: Record<WorkspaceRole, React.CSSProperties> = {
  owner: { background: 'var(--brand-50)', color: 'var(--brand-700)' },
  admin: { background: 'var(--brand-500)', color: 'var(--paper)' },
  editor: { background: 'var(--warn-50)', color: 'var(--warn-600)' },
  viewer: { background: 'var(--ink-100)', color: 'var(--ink-600)' },
};

export function RoleBadge({ role }: RoleBadgeProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        padding: '0 9px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
        ...ROLE_BADGE_STYLE[role],
      }}
    >
      {role}
    </span>
  );
}

/**
 * The inline "Saving…" status bar shown at the top of an inline-editable
 * `SectionCard tight` while a preference/timezone mutation is in flight. A
 * brand-tinted strip with a spinner; `role="status"` + `aria-live` so screen
 * readers announce the save without stealing focus.
 */
export function SectionSavingBar() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 18px',
        borderBottom: '1px solid var(--ink-100)',
        background: 'var(--brand-50)',
        fontSize: 12,
        color: 'var(--brand-700)',
        fontWeight: 500,
      }}
    >
      <span
        aria-hidden="true"
        className="animate-spin"
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          border: '2px solid var(--brand-400)',
          borderTopColor: 'transparent',
          display: 'inline-block',
        }}
      />
      Saving…
    </div>
  );
}

/**
 * An identity row inside a `SectionCard tight`: a leading visual (avatar /
 * gradient mark / icon tile), a details column (name + meta lines), and a
 * trailing action cluster. Mirrors the row scaffolding inlined across the
 * sessions / member / workspace / invite lists — `14px 18px` padding, an
 * `--ink-100` hairline divider (suppressed on the last row), and an optional
 * `--brand-50` current-row tint.
 *
 * Vertical alignment is configurable: most rows center their leading mark
 * against the details, but the org-member row top-aligns because its details
 * column can wrap onto multiple lines.
 */
type SectionIdentityRowProps = {
  leading: React.ReactNode;
  children: React.ReactNode;
  trailing?: React.ReactNode;
  /** `--brand-50` highlight (current workspace / current session). */
  current?: boolean;
  /** Suppress the bottom hairline (last row in the card). */
  last?: boolean;
  /** `center` (default) or `start` for rows whose details wrap. */
  align?: 'center' | 'start';
  /**
   * Makes the whole row clickable (e.g. expand/collapse) as a mouse/touch
   * convenience — no `role="button"`: `trailing` already carries real,
   * individually-labeled `<button>`s (a `role="button"` wrapper around other
   * buttons is an invalid nested-interactive ARIA structure, the same family
   * of issue the `asChild`-trigger rule elsewhere in this repo calls out).
   * Keyboard access stays entirely through those buttons. Each of
   * `trailing`'s own click handlers must call `stopPropagation()` or this
   * fires too — same rule as the DataTable row-click convention
   * (`use-row-navigation.ts`), which this mirrors.
   */
  onClick?: () => void;
};

export function SectionIdentityRow({
  leading,
  children,
  trailing,
  current,
  last,
  align = 'center',
  onClick,
}: SectionIdentityRowProps) {
  return (
    <div
      className={cn('flex gap-3', align === 'start' ? 'items-start' : 'items-center')}
      style={{
        padding: '14px 18px',
        background: current ? 'var(--brand-50)' : undefined,
        borderBottom: last ? '0' : '1px solid var(--ink-100)',
        cursor: onClick ? 'pointer' : undefined,
      }}
      onClick={onClick}
    >
      {leading}
      <div className="min-w-0 flex-1">{children}</div>
      {trailing}
    </div>
  );
}
